// Loads the wgpu engine's wasm viewport (engine/, from `pnpm engine:pull`, served at /engine/). Where it
// can't run (a browser without WebGPU, or with WebGPU but no GPU it can use, or a server without the
// viewport), it says why instead, and the app draws the 2D preview (the mock) and tells the visitor.

import type { Viewport, ViewportOptions } from './api.ts';

interface EngineModule {
  default: (input?: unknown) => Promise<unknown>;
  Viewport: { create(canvas: HTMLCanvasElement, options: ViewportOptions): Promise<Viewport> };
}

export const ENGINE_URL = '/engine/ba_viewport.js';

export async function engineInstalled(): Promise<boolean> {
  try {
    const res = await fetch(ENGINE_URL, { method: 'HEAD' });
    return res.ok && (res.headers.get('content-type') ?? '').includes('javascript');
  } catch {
    return false;
  }
}

/** Why a page has no wasm viewport: no WebGPU in the browser, WebGPU with no GPU it can use (hardware
 *  acceleration off, a blocked driver), no viewport on the server, or one that failed to start. */
export interface NoViewport {
  why: 'no-webgpu' | 'no-adapter' | 'no-engine' | 'failed';
  detail?: string;
}

/** Whether this browser can draw with WebGPU: it has `navigator.gpu`, and that finds an adapter. */
export async function gpuSupport(nav: { gpu?: { requestAdapter(): Promise<unknown> } } = navigator as never): Promise<'ok' | 'no-webgpu' | 'no-adapter'> {
  if (!nav.gpu) return 'no-webgpu';
  try {
    return (await nav.gpu.requestAdapter()) ? 'ok' : 'no-adapter';
  } catch {
    return 'no-adapter';
  }
}

export async function createWgpuViewport(canvas: HTMLCanvasElement, options: ViewportOptions): Promise<Viewport | NoViewport> {
  const gpu = await gpuSupport();
  if (gpu !== 'ok') return { why: gpu };
  if (!(await engineInstalled())) return { why: 'no-engine' };
  const mod = (await import(/* @vite-ignore */ ENGINE_URL)) as EngineModule;
  await mod.default();
  return mod.Viewport.create(canvas, options);
}
